// ** React Imports
import { Suspense, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

// ** Component Imports
import { FallbackLoader } from '@/shared/ui'

// ** Styled Components
import { Sidebar } from '@/widgets/Sidebar'
import { RightSidebar } from '@/widgets/right-sidebar/RightSidebar'
import { AccountExpiredOverlay } from '@/widgets/AccountExpiredOverlay'
import { FreePlanBanner } from '@/components/free-plan'
import {
	LayoutContentWrapper,
	LayoutPageContent,
	LayoutWrapper,
	TransparentButton,
	ChatBotHeaderContainer
} from './styles'
import { Menu } from '@/shared/ui/icons'

// ** Entity Imports
import { useGetCurrentUserQuery } from '@/entities/user'
import { setIsRightSidebarOpen } from '@/entities/layout'

// ** Hooks
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks'

// ** Utility Imports
import { hasAppAccess } from '@/lib/access'

// ** Third-party scripts
import { loadTawkTo } from '@/shared/lib/tawkTo'

export const LayoutWithSidebar = () => {
	const { isLoading } = useGetCurrentUserQuery()
	const dispatch = useAppDispatch()
	const isRightSidebarOpen = useAppSelector(
		(state) => state.layout.isRightSidebarOpen
	)
	const { user: currentUser } = useAppSelector((state) => state.currentUser)

	// Load the Tawk.to chat widget once the current user is known. Keyed on the
	// user id so re-renders with a new `currentUser` reference don't re-login.
	useEffect(() => {
		if (currentUser?.id) {
			loadTawkTo(currentUser)
		}
	}, [currentUser?.id])

	// Show loading state while fetching user data
	if (isLoading) {
		return <FallbackLoader />
	}

	// Check if user hasn't submitted the form yet - redirect to step 3
	// if (
	// 	currentUser &&
	// 	!currentUser.organization.isFormSubmitted &&
	// 	!currentUser.organization.isFormSkiped
	// ) {
	// 	return <Navigate to='/schedule' replace />
	// }

	// Check if user doesn't have an active subscription
	// if (
	// 	currentUser &&
	// 	!currentUser.activeSubscription &&
	// 	(currentUser.organization.isFormSubmitted ||
	// 		currentUser.organization.isFormSkiped)
	// ) {
	// 	return <Navigate to='/choose-plan' replace />
	// }

	const location = useLocation()
	const isChatBot = location.pathname.includes('/ai-chatbot')
	const isAiReport = location.pathname.includes('/ai-report')

	return (
		<LayoutWrapper>
			<AccountExpiredOverlay />
			<Sidebar />
			<LayoutContentWrapper $fullViewport={isAiReport}>
				{!isRightSidebarOpen && !isAiReport && (
					<ChatBotHeaderContainer>
						<TransparentButton
							variant='secondary'
							startIcon={<Menu />}
							onClick={() => dispatch(setIsRightSidebarOpen(true))}
						/>
					</ChatBotHeaderContainer>
				)}
				<LayoutPageContent $noPadding={isAiReport}>
					{!isAiReport && <FreePlanBanner />}
					<Suspense fallback={<FallbackLoader />}>
						<Outlet />
					</Suspense>
				</LayoutPageContent>
			</LayoutContentWrapper>
			{isRightSidebarOpen && isChatBot && hasAppAccess(currentUser) && (
				<RightSidebar
					open={isRightSidebarOpen}
					onClose={() => {
						dispatch(setIsRightSidebarOpen(false))
					}}
					activeSubscription={hasAppAccess(currentUser)}
				/>
			)}
		</LayoutWrapper>
	)
}
